import React, { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Categories, getAllCategories } from 'stores/requests/requests-store';
import { Category } from 'stores/configuration/events-n-requests/non-machine-related/clusters-store';

interface TailwindCategoryFormEditorProps {
  value: string;
  onChange?: (value: Category) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  readOnly?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindCategoryFormEditor: FC<TailwindCategoryFormEditorProps> = ({
  value,
  onChange,
  placeholder = 'Select Category',
  className = '',
  clearable = false,
  readOnly = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [categories] = useState(getAllCategories());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (selectedValue: string) => {
    // Type guard para validar que el valor es una Category válida
    const category: Category = Categories[selectedValue];
    const isCategoryValue = (value: string): value is Category => value === 'Technical' || value === 'Functional' || value === 'Unknown';

    if (isCategoryValue(category)) {
      onChange?.(category);
    }

    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const currentValue = value; // === 'Functional' || value === 'Technical' ? Categories[value] : value;

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        className={`
          relative 
          w-full 
          cursor-pointer 
          rounded-md 
          border 
          border-gray-300
          bg-white
          py-2 
          px-3 
          shadow-sm
          ${readOnly ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-blue-500'}
          ${className}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={currentValue ? `${readOnly ? 'text-gray-400' : 'text-gray-900'}` : 'text-gray-400'}>
            {currentValue || t(placeholder)}
          </span>
          <div className="flex items-center">
            {clearable && currentValue && !readOnly && (
              <button onClick={handleClear} className="p-1 hover:text-gray-600 text-gray-400" type="button"></button>
            )}
          </div>
        </div>
      </div>

      {isOpen && !readOnly && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
          {categories.map(category => (
            <div
              key={category.value as string}
              className={`
                px-4 
                py-2 
                cursor-pointer 
                hover:bg-gray-100
                ${currentValue === category.value ? 'bg-blue-50 text-black-700' : 'text-gray-900'}
              `}
              onClick={() => handleSelect(category.value)}
            >
              {t(category.text as string)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TailwindCategoryFormEditor;
